import React, { useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2, X } from 'lucide-react';
import Toast from './Toast';
import taskService from '../services/taskService';
import { useTranslation } from '../i18n/LocaleContext';

// 幸运订单 USDT-TRC20 链上支付弹层，壳子照抄 DepositModal 的底部抽屉。
// 打开时向后端要「收款地址 + 本单唯一支付金额」，之后每 5s 轮询订单状态，
// 链上监听确认到账（status=paid）即回调 onPaid 关闭。
const LuckyPayModal = ({ isOpen, orderId, onClose, onPaid }) => {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(false);
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setAnimating(true), 10);
      return undefined;
    }
    setAnimating(false);
    const timer = setTimeout(() => setVisible(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !orderId) return undefined;
    let cancelled = false;
    setInfo(null);
    taskService.startLuckyPayment(orderId)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setSecondsLeft(data.expiresInSec || 300);
      })
      .catch((error) => {
        if (cancelled) return;
        setToast({
          type: 'error',
          message: error.errorCode === 'PAY_UNAVAILABLE' ? t('luckyPayUnavailable') : error.message,
        });
      });
    return () => { cancelled = true; };
  }, [isOpen, orderId, t]);

  useEffect(() => {
    if (!isOpen || !orderId) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const res = await taskService.getLuckyOrder(orderId);
        if (res.status === 'paid') {
          clearInterval(pollRef.current);
          onPaid();
        }
      } catch (error) {
        // 轮询瞬时失败忽略，下一轮再试
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [isOpen, orderId, onPaid]);

  // 5 分钟支付会话倒计时：归零即关闭，重新打开会续一个新会话
  useEffect(() => {
    if (!isOpen || !info || secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => {
      if (secondsLeft === 1) {
        setToast({ type: 'error', message: t('luckyPayExpired') });
        onClose();
      } else {
        setSecondsLeft((n) => n - 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [isOpen, info, secondsLeft, t, onClose]);

  const copy = (text, setCopied) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {visible && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${animating ? 'opacity-50' : 'opacity-0'}`}
            onClick={onClose}
          />
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-lift flex flex-col max-h-[92vh] transform transition-transform duration-300 ease-out ${
              animating ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="text-lg font-bold text-ink">{t('luckyPayTitle')}</h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full text-ink-muted hover:bg-canvas transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-6">
              {!info ? (
                <div className="py-16 flex flex-col items-center gap-3 text-ink-muted">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <p className="text-[13px]">{t('luckyPayLoading')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl bg-canvas p-4">
                    <p className="text-[12px] text-ink-muted">{t('luckyPayAmountLabel')}</p>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <p className="text-[24px] leading-none font-extrabold text-money tabular-nums">
                        {info.payAmount.toFixed(2)} USDT
                      </p>
                      <button
                        onClick={() => copy(info.payAmount.toFixed(2), setCopiedAmount)}
                        className="p-2 rounded-full text-brand-600 hover:bg-white transition-colors"
                      >
                        {copiedAmount ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[11px] text-ink-muted">{t('luckyPayAmountHint')}</p>
                  </div>

                  <div className="flex justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(info.address)}`}
                      alt="QR Code"
                      className="w-[200px] h-[200px] rounded-xl"
                    />
                  </div>

                  <div className="rounded-xl border border-black/[0.06] p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-[12px] text-ink-muted">{t('luckyPayAddressLabel')}</p>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                        {t('luckyPayNetwork')}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-start justify-between gap-3">
                      <p className="break-all text-[13px] font-medium text-ink">{info.address}</p>
                      <button
                        onClick={() => copy(info.address, setCopiedAddr)}
                        className="shrink-0 p-2 rounded-full text-brand-600 hover:bg-canvas transition-colors"
                      >
                        {copiedAddr ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 py-1 text-ink-muted">
                    <span className="flex gap-1">
                      <span className="queue-dot h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                      <span className="queue-dot h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animationDelay: '0.15s' }} />
                      <span className="queue-dot h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animationDelay: '0.3s' }} />
                    </span>
                    <p className="text-[13px]">
                      {t('luckyPayWaiting')} · {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LuckyPayModal;
